import React, { createContext, useContext } from 'react';
import useFetchPreRegisteredUserForTeacher from '../hooks/useFetchPreRegisteredUserForTeacher';
import useAllStudentGoalsData from '../hooks/useAllStudentGoalsData';
import AuthContext from './AuthContext';

const UserDataContext = createContext();

export const UserDataProvider = ({ children }) => {
  const { user, typeOfUser } = useContext(AuthContext);

  // Use the hook without passing options - the hook has its own optimized settings
  const preRegisteredUsersQuery = useFetchPreRegisteredUserForTeacher(
    user?.uid
  );

  // Fetch full student data once and derive basic data from it
  // This prevents duplicate API calls while providing both data types
  const studentGoalsQuery = useAllStudentGoalsData(user?.uid, typeOfUser, {
    // Only fetch if we have both uid and typeOfUser
    enabled: !!(user?.uid && typeOfUser),
    // Include goals data so we can see goals from students list
    includeDataCollection: true,
    includeGoalData: true,
  });

  // Derive basic student data from the full data to avoid duplicate API calls
  const basicStudentDataQuery = {
    ...studentGoalsQuery,
    data: studentGoalsQuery.data
      ? studentGoalsQuery.data.map((student) => ({
          id: student.id,
          name: student.name,
          email: student.email,
          // Include only basic fields, exclude dataCollection and goalData
          ...Object.fromEntries(
            Object.entries(student).filter(
              ([key]) => !['dataCollection', 'goalData'].includes(key)
            )
          ),
        }))
      : null,
  };

  const contextValue = {
    preRegisteredUsers: preRegisteredUsersQuery,
    studentGoals: studentGoalsQuery,
    basicStudentData: basicStudentDataQuery,
  };

  return (
    <UserDataContext.Provider value={contextValue}>
      {children}
    </UserDataContext.Provider>
  );
};

export const usePreRegisteredUsers = () => {
  const context = useContext(UserDataContext);
  if (context === undefined) {
    throw new Error(
      'usePreRegisteredUsers must be used within a UserDataProvider'
    );
  }
  return context.preRegisteredUsers;
};

export const useStudentGoals = () => {
  const context = useContext(UserDataContext);
  if (context === undefined) {
    throw new Error('useStudentGoals must be used within a UserDataProvider');
  }
  // Return basic student data by default for better performance
  // This maintains backward compatibility while providing optimized data
  return context.basicStudentData;
};

export const useBasicStudentDataFromContext = () => {
  const context = useContext(UserDataContext);
  if (context === undefined) {
    throw new Error(
      'useBasicStudentDataFromContext must be used within a UserDataProvider'
    );
  }
  return context.basicStudentData;
};

// For components that need full student data with goals and data collection
export const useFullStudentGoals = () => {
  const context = useContext(UserDataContext);
  if (context === undefined) {
    throw new Error(
      'useFullStudentGoals must be used within a UserDataProvider'
    );
  }
  return context.studentGoals;
};

export const useUserData = () => {
  const context = useContext(UserDataContext);
  if (context === undefined) {
    throw new Error('useUserData must be used within a UserDataProvider');
  }
  return context;
};
